import {
  ErrorTagUtil,
  FieldHtmlNameUtil,
  FormIdUtil,
  type WwwHeartbeatData,
} from '@onshape/www-inertia';
import errorReporter from '~/utils/AaaSentryIo';

type FieldParam = { fieldName: string; fieldValue: string | undefined | boolean | number };
type EventDispatcher = (_param: FieldParam) => void;
// cache of the latest values for form injections
const formInjectParamsLatest: Array<FieldParam> = [];

const dispatchEvent: EventDispatcher = ({ fieldName, fieldValue }) => {
  const bus = useHekateEventBus();
  const oldIndex = formInjectParamsLatest.findIndex((ff) => ff.fieldName === fieldName);
  if (oldIndex !== -1) {
    formInjectParamsLatest.splice(oldIndex, 1);
  }

  formInjectParamsLatest.push({ fieldName, fieldValue });

  bus.dispatchEvent('form-inject-value', {
    fieldName,
    fieldValue,
  });
};

const initializeHekateInjectionsEvents = () => {
  const bus = useHekateEventBus();
  bus.addEventListener('request-form-injections', () => {
    formInjectParamsLatest.forEach((params) => {
      bus.dispatchEvent('form-inject-value', {
        fieldName: params.fieldName,
        fieldValue: params.fieldValue,
      });
    });
    bus.dispatchEvent('request-form-injections-sent', {});
  });
};

const preferenceCenterWatcher = async () => {
  const { user, setParamsForFetch } = useCrossSession();
  setParamsForFetch({
    params: ['emailPreferences'],
  });

  watch(user, (val) => {
    if (!val) return;

    dispatchEvent({
      fieldName: 'email',
      fieldValue: val.email || '',
    });

    if (!val.eloquaExtraData) return;

    const { eloquaExtraData } = val;
    dispatchEvent({
      fieldName: 'isEmailPreferencesSet',
      fieldValue: eloquaExtraData.isEmailPreferencesSet,
    });
    dispatchEvent({
      fieldName: 'isSubBlogs',
      fieldValue: eloquaExtraData.isSubBlogs,
    });
    dispatchEvent({
      fieldName: 'isSubEvents',
      fieldValue: eloquaExtraData.isSubEvents,
    });
    dispatchEvent({
      fieldName: 'isSubNewsletter',
      fieldValue: eloquaExtraData.isSubNewsletter,
    });
    dispatchEvent({
      fieldName: 'isSubPremiumContent',
      fieldValue: eloquaExtraData.isSubPremiumContent,
    });
  });
};

const formLocationInjections = async () => {
  const url = useRequestURL();
  url.search = '';
  url.hash = '';

  dispatchEvent({
    fieldName: FieldHtmlNameUtil.referringWwwFormUrl,
    fieldValue: url.href,
  });
  dispatchEvent({
    fieldName: 'mostrecentleadsource',
    fieldValue: url.href,
  });
  dispatchEvent({
    fieldName: 'contentdownload',
    fieldValue: url.href,
  });
};

const siteLanguageFormInjection = async () => {
  const lang = useSiteLang().lang.value;

  dispatchEvent({
    fieldName: 'wwwSiteLanguage',
    fieldValue: lang,
  });
  dispatchEvent({
    fieldName: 'navigatorLanguage',
    fieldValue: window?.navigator?.language ? window.navigator.language : '',
  });
};

type OffsiteReferringUrlStorage = {
  url: string;
  creationTime: number;
};

const offsiteReferrerFormInjection = async () => {
  // get from document.referrer and if its not for the current site, return it so it can be stored in localStorage
  const getReferrerFromDocument = (): string => {
    if (!window || !document) {
      return '';
    }
    try {
      const docReferrer = document.referrer;
      if (!docReferrer) {
        return '';
      }
      const refUrl = new URL(docReferrer);
      if (!refUrl.hostname) {
        return '';
      }
      if (refUrl.hostname === window.location.hostname) {
        // If the referrer is the same as the current site, return empty string
        return '';
      }
      return refUrl.href;
    } catch (e) {
      console.warn('Failed to get referrer from document', e);
      return '';
    }
  };
  const getReferrer = () => {
    const { hasLocalStorage, getFromLocalStorageOrNull, setToLocalStorage } = useWwwStorage();
    if (!hasLocalStorage.value) {
      console.warn('No local storage available');
      return '';
    }
    // NOTE: THe local storage is set in Google Tag Manager as OffsiteReferringUrl (Full URL of Offsite Referrer)
    const now = new Date().getTime();
    try {
      let fromStorage = getFromLocalStorageOrNull<OffsiteReferringUrlStorage>({
        key: 'OffsiteReferringUrl',
      });

      if (!fromStorage || !fromStorage.url) {
        const docReferrer = getReferrerFromDocument();
        if (!docReferrer) {
          return '';
        }
        setToLocalStorage<OffsiteReferringUrlStorage>({
          key: 'OffsiteReferringUrl',
          data: {
            url: docReferrer,
            creationTime: now,
          },
          ttl: 24 * 60 * 60 * 1000, // 1 day
        });

        fromStorage = getFromLocalStorageOrNull<OffsiteReferringUrlStorage>({
          key: 'OffsiteReferringUrl',
        });
      }

      if (!fromStorage) {
        return '';
      }

      const expiry = 24 * 60 * 60 * 1000;
      const { creationTime } = fromStorage;
      if (creationTime + expiry < now) {
        return '';
      }
      const url = new URL(fromStorage.url);
      if (!url.hostname) {
        return '';
      }

      return url.href;
    } catch (e) {
      console.error('Error getting offsite referrer from localStorage', e);
      return '';
    }
  };

  const referrer = getReferrer();
  dispatchEvent({
    fieldName: 'offsiteReferringUrl',
    fieldValue: referrer,
  });
};

const heartBeatFormInjection = async () => {
  const bus = useHekateEventBus();
  const initRefreshCsrfTokenListener = () => {
    bus.addEventListener('refresh-csrf-token', () => {
      console.log('Refreshing token');
      try {
        const { refreshHeartbeat } = useCrossSession();
        refreshHeartbeat();
      } catch (e) {
        errorReporter.postToSentry({
          tag: ErrorTagUtil.heartbeat,
          msg: 'Could not find heartbeat, refresh-csrf-token, caught error',
          error: e as Error,
        });
      }
    });
  };

  const initHeartbeatWatcher = () => {
    const { heartbeat } = useCrossSession();

    watch(heartbeat, async (newHeartbeat) => {
      if (!newHeartbeat) return;

      const { csrfToken } = newHeartbeat;
      if (csrfToken) {
        window.csrfToken = csrfToken;
        const { heartbeat } = useCrossSession();
        let heartBeatData: WwwHeartbeatData | null = null;
        try {
          heartBeatData = await FluentWaitUtil.whileNotFalsy({
            provider: () => heartbeat.value,
            timeBetweenChecksInMilliseconds: 100,
            maxTries: 600,
          });
          if (!heartBeatData) {
            throw new Error('Heartbeat data is null');
          }
        } catch (e) {
          errorReporter.postToSentry({
            tag: ErrorTagUtil.heartbeat,
            msg: 'Could not find heartbeat, getHiddenFormFieldData',
            error: e as Error,
          });
        }

        if (!heartBeatData) {
          console.warn('Heartbeat data is null, cannot inject values');
          return;
        }

        dispatchEvent({
          fieldName: 'csrfToken',
          fieldValue: heartBeatData.csrfToken || '',
        });

        dispatchEvent({
          fieldName: 'Ipaddress',
          fieldValue: heartBeatData.ip || '',
        });

        dispatchEvent({
          fieldName: 'geocountry',
          fieldValue: heartBeatData.country || 'testCountry',
        });

        dispatchEvent({
          fieldName: 'countryCode',
          fieldValue: heartBeatData.countryCode || '',
        });

        dispatchEvent({
          fieldName: 'geostate',
          fieldValue: heartBeatData.regionName || '',
        });

        dispatchEvent({
          fieldName: 'geocity',
          fieldValue: heartBeatData.city || '',
        });
      } else {
        errorReporter.postToSentry({
          tag: ErrorTagUtil.heartbeat,
          msg: 'Could not find heartbeat, refresh-csrf-token',
        });
      }
    });
  };

  initRefreshCsrfTokenListener();
  initHeartbeatWatcher();
};

const queryStringTrackingFormInjection = async () => {
  const queryStringKeyToFieldName = (queryStringKey: string): string => {
    switch (queryStringKey) {
      case 'var_identifier':
        return 'paternerdetail';
      case 'gclid':
        return 'utm_gclid';
      default:
        return queryStringKey;
    }
  };

  const trackedQueryStringKeys: string[] = [
    'utm_campaign',
    'utm_content',
    'utm_medium',
    'utm_source',
    'utm_term',
    'gclid',
    'var_identifier',
  ];

  const params: FormInjectionParams[] = [];

  const localStorageKeyName: LocalStorageKeyName = 'HekateQueryStringFormInjection';
  const maxTries = 20;
  const timeBetweenTriesInMilliseconds = 500;

  const localStorageExpiryInMilliseconds = 1000 * 60 * 60 * 24 * 30;

  let antiFloodFlag = false;

  const documentUrl = useRequestURL();

  const queryLocalStorageForParams = (): FormInjectionParams[] => {
    // if no params are found return the empty list
    const notFound: FormInjectionParams[] = [];
    const { hasLocalStorage, getFromLocalStorageOrNull, removeFromLocalStorage } = useWwwStorage();
    if (!hasLocalStorage.value) {
      return notFound;
    }

    const store = getFromLocalStorageOrNull({
      key: localStorageKeyName,
    });
    if (!store) {
      return notFound;
    }

    try {
      const utmData = store as { params: FormInjectionParams[]; dateAdded: number };
      if (!utmData.dateAdded) {
        return notFound;
      }

      const storageAge = Date.now() - utmData.dateAdded;
      const isExpired = storageAge > localStorageExpiryInMilliseconds;
      if (isExpired) {
        removeFromLocalStorage({
          key: localStorageKeyName,
        });
        return notFound;
      }
      return utmData.params;
    } catch (e) {
      console.warn(e);
      return notFound;
    }
  };

  const pollLocalStorageForParams = (): Promise<FormInjectionParams[]> => {
    let curTry = maxTries;
    return new Promise((res) => {
      const intervalHandle = setInterval(() => {
        if (curTry < 1) {
          clearInterval(intervalHandle);
          res([]);
        }
        curTry -= 1;

        const params = queryLocalStorageForParams();
        if (params.length > 0) {
          clearInterval(intervalHandle);
          res(params);
        }
      }, timeBetweenTriesInMilliseconds);
    });
  };

  const storeParamsInLocalStorage = (params: FormInjectionParams[]): boolean => {
    const { hasLocalStorage, setToLocalStorage } = useWwwStorage();
    if (!hasLocalStorage.value) {
      return false;
    }
    setToLocalStorage({
      key: localStorageKeyName,
      data: {
        params,
        dateAdded: Date.now(),
      },
      ttl: 1000 * 60 * 60 * 24 * 30, // 30 days
    });

    return true;
  };

  const getFormFieldData = (): Promise<FormInjectionParams[]> => {
    trackedQueryStringKeys.forEach((fieldName) => {
      if (!documentUrl.searchParams.has(fieldName)) {
        return;
      }

      if (documentUrl.searchParams.getAll(fieldName).length > 1 && !antiFloodFlag) {
        antiFloodFlag = true;
        setTimeout(() => {
          if (Math.random() < 0.01) {
            // anti-flood
            errorReporter.postToSentry({
              tag: ErrorTagUtil.utmDupe,
              msg: 'Duplicate UTM field name',
              error: new Error(`Duplicate UTM field name: ${fieldName} - ${documentUrl.href}`),
            });
          }
        }, 100);
      }

      const fieldValue = documentUrl.searchParams.get(fieldName);
      if (!fieldValue) {
        return;
      }

      const fieldParams: FormInjectionParams = {
        fieldParams: {
          fieldName: queryStringKeyToFieldName(fieldName),
          fieldValue,
        },
      };

      params.push(fieldParams);
    });

    if (params.length > 0) {
      // if we don't have localStorage then just resolve
      if (!storeParamsInLocalStorage(params)) {
        return Promise.resolve(params);
      }
    }

    // try to pull params from localStorage (possibly having just been set)
    return pollLocalStorageForParams();
  };

  const injections = await getFormFieldData();
  injections.forEach((param) => {
    dispatchEvent({
      fieldName: param.fieldParams.fieldName,
      fieldValue: param.fieldParams.fieldValue,
    });
  });
};

const vwoTestFormInjection = async () => {
  const vwoUuidFieldName = 'vwoUuid';
  const experimentIdFieldName = 'experimentId';
  const abVersionFieldName = 'abVersion';
  const vwoTestInfoStorageKey: SessionStorageKeyName = 'VwoTestInfo';

  const { haveVwo } = useVwoTest();
  const { getFromSessionStorageOrNull } = useWwwStorage();
  let vwoUuid = 'unset';
  let experimentId = 'unset';
  let abVersion = 'unset';

  if (!haveVwo) {
    vwoUuid = 'not loaded';
    experimentId = 'not loaded';
    abVersion = 'not loaded';
  }
  const testInfo = getFromSessionStorageOrNull({
    key: vwoTestInfoStorageKey,
  }) as VwoTestInfo;
  // vwo loaded but not in test
  if (!testInfo || !testInfo.isInTest) {
    vwoUuid = 'no test';
    experimentId = 'no test';
    abVersion = 'no test';
  }

  // vwo loaded and user in test
  if (testInfo) {
    vwoUuid = testInfo.vwoUuid;
    experimentId = testInfo.experimentId;
    abVersion = testInfo.variationId;
  }

  const sendTrackingEvents = (vwoUuid: string, experimentId: string, abVersion: string): void => {
    const { mouseFlowPush, dataLayerPush } = useTracking();

    mouseFlowPush(['setVariable', 'vwo-ab-version', abVersion, 'session', false]);
    mouseFlowPush(['setVariable', 'vwo-experiment-id', experimentId, 'session', false]);
    mouseFlowPush(['setVariable', 'vwo-uuid', vwoUuid, 'session', false]);

    dataLayerPush({
      event: 'ABTest',
      category: 'A/B test',
      action: 'Viewed Version',
      label: abVersion || '',
    });

    dataLayerPush({
      event: 'ABTest',
      category: 'A/B test',
      action: 'Experiment Entry',
      label: experimentId || '',
    });

    dataLayerPush({
      event: 'ABTest',
      category: 'A/B test',
      action: 'VWO UUID',
      label: vwoUuid || '',
    });
  };

  sendTrackingEvents(vwoUuid, experimentId, abVersion);

  const injections = [
    {
      fieldParams: {
        fieldName: vwoUuidFieldName,
        fieldValue: vwoUuid,
      },
    },
    {
      fieldParams: {
        fieldName: experimentIdFieldName,
        fieldValue: experimentId,
      },
    },
    {
      fieldParams: {
        fieldName: abVersionFieldName,
        fieldValue: abVersion,
      },
    },
  ];

  injections.forEach((injection) => {
    dispatchEvent({
      fieldName: injection.fieldParams.fieldName,
      fieldValue: injection.fieldParams.fieldValue,
    });
  });
};

/** Initialize all global form injections */
const globalFormInjections = async () => {
  await Promise.all([
    siteLanguageFormInjection(),
    formLocationInjections(),
    offsiteReferrerFormInjection(),
    heartBeatFormInjection(),
    queryStringTrackingFormInjection(),
    vwoTestFormInjection(),
  ]);
};

let isInit = false;
export const useFormInjections = async ({
  formId,
  formVariant,
}: {
  formId: string;
  formVariant?: string;
}) => {
  if (import.meta.server) {
    // console.warn('useFormInjection cannot be used on the server side, skipping');
    return {};
  }

  // inject form variant as early as possible
  dispatchEvent({
    fieldName: FieldHtmlNameUtil.formVariant,
    fieldValue: formVariant || '',
  });

  if (!isInit) {
    initializeHekateInjectionsEvents();
    isInit = true;
  }

  // handle Form injections that should run on every form
  await globalFormInjections();

  // initialize the correct form injection watchers based on formId
  switch (formId) {
    case FormIdUtil.names.emailSubscriptionPreferences:
      preferenceCenterWatcher();
      return;
    default:
      // console.warn(`No form injection watcher for formId: ${formId}`);
      break;
  }

  useHekateEventBus().dispatchEvent('request-form-injections', {});
};
